import { useState } from 'react';
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import {
  History,
  Download,
  Filter,
  Calendar,
  User,
  Award,
  ChevronLeft,
  ChevronRight,
  Eye
} from "lucide-react";
import { cn } from "@/lib/utils";

function CelebrationAwardsIndex({
  celebration_awards,
  stats,
  filters = {},
  event_types,
  pagination
}) {
  const [selectedEventType, setSelectedEventType] = useState(filters.event_type || '');
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '');
  const [selectedDate, setSelectedDate] = useState(filters.date || '');

  const applyFilters = () => {
    const params = {};
    if (selectedEventType) params.event_type = selectedEventType;
    if (selectedStatus) params.status = selectedStatus;
    if (selectedDate) params.date = selectedDate;

    router.get('/admin/celebration_awards', params, {
      preserveState: true,
      preserveScroll: true
    });
  };

  const clearFilters = () => {
    setSelectedEventType('');
    setSelectedStatus('');
    setSelectedDate('');
    router.get('/admin/celebration_awards', {}, {
      preserveState: true,
      preserveScroll: true
    });
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (selectedEventType) params.append('event_type', selectedEventType);
    if (selectedStatus) params.append('status', selectedStatus);
    if (selectedDate) params.append('date', selectedDate);

    window.location.href = `/admin/celebration_awards.csv?${params.toString()}`;
  };

  const viewDetails = (awardId) => {
    router.visit(`/admin/celebration_awards/${awardId}`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      skipped: 'bg-gray-100 text-gray-700'
    };

    const labels = {
      completed: '✅ Completado',
      failed: '❌ Fallido',
      skipped: '⏭️ Omitido'
    };

    return (
      <Badge className={cn('text-xs', styles[status] || styles.skipped)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getEventTypeEmoji = (triggerType) => {
    const emojis = {
      birthday: '🎂',
      work_anniversary: '🎉',
      new_user: '👋',
      custom_date: '📅'
    };
    return emojis[triggerType] || '🎁';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasActiveFilters = selectedEventType || selectedStatus || selectedDate;

  return (
    <div className="space-y-6">
      {/* Header con Stats */}
      <Card className="border-t-4 border-t-purple-500 shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center text-xl">
                <History className="w-5 h-5 mr-2 text-purple-500" />
                Historial de Celebraciones
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Registro completo de todas las celebraciones otorgadas
              </p>
            </div>
            <Button
              onClick={exportCSV}
              className="bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>

        {/* Stats Cards */}
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600 font-medium">Total Celebraciones</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.total}</p>
                </div>
                <Award className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-medium">Completadas</p>
                  <p className="text-2xl font-bold text-green-900">{stats.completed}</p>
                </div>
                <Award className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600 font-medium">Fallidas</p>
                  <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
                </div>
                <Award className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-700">Filtros</h3>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-xs text-gray-600 hover:text-gray-900"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Filtro por tipo de evento */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tipo de Evento
                </label>
                <select
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Todos</option>
                  {event_types.map(([value, label]) => (
                    <option key={value} value={value}>
                      {getEventTypeEmoji(value)} {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtro por estado */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Todos</option>
                  <option value="completed">✅ Completado</option>
                  <option value="failed">❌ Fallido</option>
                  <option value="skipped">⏭️ Omitido</option>
                </select>
              </div>

              {/* Filtro por fecha */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Botón aplicar */}
              <div className="flex items-end">
                <Button
                  onClick={applyFilters}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                >
                  Aplicar Filtros
                </Button>
              </div>
            </div>
          </div>

          {/* Tabla de Awards */}
          {celebration_awards.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      <User className="w-4 h-4 inline mr-1" />
                      Usuario
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      <Award className="w-4 h-4 inline mr-1" />
                      Evento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      Puntos
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {celebration_awards.map((award) => (
                    <tr key={award.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {award.user.avatar_url ? (
                            <img
                              src={award.user.avatar_url}
                              alt={award.user.full_name}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold text-xs">
                              {award.user.full_name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{award.user.full_name}</p>
                            <p className="text-xs text-gray-500">{award.user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {getEventTypeEmoji(award.celebration_event.event_type)}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900 text-xs">
                              {award.celebration_event.event_type}
                            </p>
                            {award.celebration_event.hashtag && (
                              <p className="text-xs text-purple-600">{award.celebration_event.hashtag}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                          {award.points_awarded} pts
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatDate(award.celebrated_date)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(award.status)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          onClick={() => viewDetails(award.id)}
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 hover:bg-purple-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginación */}
              {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 px-4 py-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    Página {pagination.current_page} de {pagination.total_pages}
                    <span className="ml-2 text-gray-500">
                      ({pagination.total_count} total)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => router.get(`/admin/celebration_awards?page=${pagination.current_page - 1}`)}
                      disabled={!pagination.prev_page}
                      variant="ghost"
                      size="sm"
                      className="text-gray-600"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </Button>
                    <Button
                      onClick={() => router.get(`/admin/celebration_awards?page=${pagination.current_page + 1}`)}
                      disabled={!pagination.next_page}
                      variant="ghost"
                      size="sm"
                      className="text-gray-600"
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No se encontraron celebraciones</p>
              <p className="text-sm mt-1">
                {hasActiveFilters
                  ? 'Intenta ajustar los filtros para ver más resultados'
                  : 'Las celebraciones aparecerán aquí cuando se otorguen'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats por tipo de evento */}
      {stats.by_event_type && stats.by_event_type.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Distribución por Tipo de Evento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {stats.by_event_type.map((stat) => (
                <div
                  key={stat.event_type}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200 text-center"
                >
                  <div className="text-2xl mb-1">
                    {getEventTypeEmoji(stat.event_type)}
                  </div>
                  <div className="text-xs text-gray-600 mb-1">
                    {stat.event_type.replace(/_/g, ' ')}
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {stat.count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

CelebrationAwardsIndex.layout = page => <AppLayout children={page} />;

export default CelebrationAwardsIndex;
